/**
 * Language Selection Dialog - Popup for choosing preferred language
 *
 * Matches the UX pattern of EditProfileDialog:
 * - Opens as a centered popup overlay
 * - Shows supported languages with checkmark on selected
 * - On save, updates user's preferred_language via profile API
 * - Updates i18n language and Redux user state
 */

import { Check, Loader2 } from 'lucide-react';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, useToast } from '@/components/ui';
import { changeLanguage, supportedLanguages } from '@/i18n';
import { updateUserProfile } from '@/service/AuthenticationService';
import { useAppSelector, useAppDispatch, selectUser, setUser } from '@/store';
import { cn } from '@/utils/cn';

interface LanguageDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LanguageDialog({ isOpen, onClose }: LanguageDialogProps) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const user = useAppSelector(selectUser);

  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [isSaving, setIsSaving] = useState(false);

  const hasChanged = selectedLanguage !== i18n.language;

  const handleSave = useCallback(async () => {
    if (!hasChanged || !user || isSaving) return;

    setIsSaving(true);

    try {
      const response = await updateUserProfile({
        _id: user._id,
        first_name: user.first_name,
        last_name: user.last_name,
        country_code: user.country_code,
        mobile_no: user.mobile_no,
        email: user.email ?? '',
        carrier: user.carrier?.map((c) => c._id) ?? [],
        plants: user.plants?.map((p) => p._id) ?? [],
        preferred_language: selectedLanguage,
      });

      if (response.status) {
        // Update i18n language and persist to localStorage
        void changeLanguage(selectedLanguage);

        // Update Redux user state with new preferred_language
        dispatch(setUser({ ...user, preferred_language: selectedLanguage }));
        onClose();
      } else {
        showToast(response.message || t('language.failedToUpdate'), { type: 'error' });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('language.failedToUpdate');
      showToast(message, { type: 'error' });
      // Revert selection on failure
      setSelectedLanguage(i18n.language);
    } finally {
      setIsSaving(false);
    }
  }, [hasChanged, user, isSaving, selectedLanguage, i18n.language, dispatch, onClose, showToast]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center py-4"
      role="button"
      tabIndex={0}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="w-[85vw] max-w-[360px] bg-white rounded-xl shadow-xl overflow-hidden"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <h2 className="text-lg font-semibold font-poppins text-[#0B2F50]">
            {t('language.title', 'Language')}
          </h2>
        </div>

        {/* Language Options */}
        <div className="px-3 pb-3">
          {supportedLanguages.map((lang) => {
            const isSelected = selectedLanguage === lang.code;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => {
                  setSelectedLanguage(lang.code);
                }}
                disabled={isSaving}
                className={cn(
                  'w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors',
                  isSelected ? 'bg-[#E8F0FE]' : 'hover:bg-gray-50'
                )}
              >
                <div className="flex flex-col items-start">
                  <span
                    className={cn(
                      'text-sm',
                      isSelected ? 'text-[#1357A0] font-semibold' : 'text-[#0B2F50] font-medium'
                    )}
                  >
                    {lang.nativeName}
                  </span>
                  <span className="text-xs text-[#647d8f]">{lang.name}</span>
                </div>
                {isSelected && (
                  <Check className="h-5 w-5 text-[#1357A0] flex-shrink-0" strokeWidth={3} />
                )}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 px-5 pb-5 pt-2">
          <Button
            variant="outline"
            className="flex-1 h-10 border-[1.35px] border-[#003366] rounded-md text-xs text-[#104070] font-medium bg-white hover:bg-gray-50"
            onClick={onClose}
            disabled={isSaving}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            className={cn(
              'flex-1 h-10 border-[1.3px] rounded-md text-xs text-white font-medium',
              hasChanged
                ? 'bg-[#003366] hover:bg-[#002855] border-[#003366]'
                : 'bg-[#ababab] border-[#ababab]'
            )}
            onClick={() => {
              void handleSave();
            }}
            disabled={!hasChanged || isSaving}
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save', 'Save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
